import { useEffect, useLayoutEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import {
  addDelta,
  metersToDelta,
  NUDGE_STEP_LARGE_METERS,
  NUDGE_STEP_METERS,
} from "./shiftGeometry";

const SHIFT_PANES = ["overlayPane", "markerPane", "shadowPane", "tooltipPane"];

const CourseShiftLayer = ({ phase, delta, setDelta }) => {
  const map = useMap();
  const deltaRef = useRef(delta);
  deltaRef.current = delta;
  const spaceHeldRef = useRef(false);
  const anchorRef = useRef(null);

  const isAdjusting = phase === "adjusting";
  const isPreviewShown =
    phase === "adjusting" || phase === "processing" || phase === "settling";

  useEffect(() => {
    if (!isPreviewShown) return undefined;
    const container = map.getContainer();
    const draggingWasEnabled = map.dragging.enabled();
    const keyboardWasEnabled = map.keyboard ? map.keyboard.enabled() : false;
    anchorRef.current = map.getCenter();
    map.dragging.disable();
    if (map.keyboard) map.keyboard.disable();
    container.style.cursor = "move";
    SHIFT_PANES.forEach((name) => {
      const pane = map.getPane(name);
      if (pane) pane.style.pointerEvents = "none";
    });
    return () => {
      container.style.cursor = "";
      SHIFT_PANES.forEach((name) => {
        const pane = map.getPane(name);
        if (pane) pane.style.pointerEvents = "";
      });
      if (draggingWasEnabled) map.dragging.enable();
      if (keyboardWasEnabled) map.keyboard.enable();
      anchorRef.current = null;
    };
  }, [map, isPreviewShown]);

  useLayoutEffect(() => {
    if (!isPreviewShown) return undefined;
    const applyTransform = () => {
      const current = deltaRef.current;
      const anchor = anchorRef.current || map.getCenter();
      const base = map.project(anchor);
      const moved = map.project([
        anchor.lat + current.deltaLat,
        anchor.lng + current.deltaLng,
      ]);
      const x = moved.x - base.x;
      const y = moved.y - base.y;
      SHIFT_PANES.forEach((name) => {
        const pane = map.getPane(name);
        if (pane) pane.style.transform = `translate(${x}px, ${y}px)`;
      });
    };
    applyTransform();
    map.on("zoomend", applyTransform);
    return () => {
      map.off("zoomend", applyTransform);
      SHIFT_PANES.forEach((name) => {
        const pane = map.getPane(name);
        if (pane) pane.style.transform = "";
      });
    };
  }, [map, isPreviewShown, delta]);

  useEffect(() => {
    if (!isAdjusting) return undefined;
    const container = map.getContainer();
    let dragStartPoint = null;
    let dragStartDelta = null;

    const toContainerPoint = (event) => {
      const rect = container.getBoundingClientRect();
      return [event.clientX - rect.left, event.clientY - rect.top];
    };

    const onMouseDown = (event) => {
      if (event.button !== 0 || spaceHeldRef.current) return;
      dragStartPoint = toContainerPoint(event);
      dragStartDelta = deltaRef.current;
      event.preventDefault();
    };

    const onMouseMove = (event) => {
      if (!dragStartPoint) return;
      const from = map.containerPointToLatLng(dragStartPoint);
      const to = map.containerPointToLatLng(toContainerPoint(event));
      setDelta(
        addDelta(dragStartDelta, {
          deltaLat: to.lat - from.lat,
          deltaLng: to.lng - from.lng,
        })
      );
    };

    const onMouseUp = () => {
      dragStartPoint = null;
      dragStartDelta = null;
    };

    const onKeyDown = (event) => {
      const tag = event.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (event.code === "Space") {
        if (!spaceHeldRef.current) {
          spaceHeldRef.current = true;
          map.dragging.enable();
          container.style.cursor = "grab";
        }
        event.preventDefault();
        return;
      }
      const step = event.shiftKey ? NUDGE_STEP_LARGE_METERS : NUDGE_STEP_METERS;
      const nudges = {
        ArrowUp: [step, 0],
        ArrowDown: [-step, 0],
        ArrowLeft: [0, -step],
        ArrowRight: [0, step],
      };
      const move = nudges[event.key];
      if (!move) return;
      event.preventDefault();
      const refLat = map.getCenter().lat;
      setDelta(
        addDelta(deltaRef.current, metersToDelta(move[0], move[1], refLat))
      );
    };

    const onKeyUp = (event) => {
      if (event.code !== "Space" || !spaceHeldRef.current) return;
      spaceHeldRef.current = false;
      map.dragging.disable();
      container.style.cursor = "move";
    };

    container.addEventListener("mousedown", onMouseDown);
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("keyup", onKeyUp);
    return () => {
      container.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("keyup", onKeyUp);
      spaceHeldRef.current = false;
    };
  }, [map, isAdjusting, setDelta]);

  return null;
};

export default CourseShiftLayer;
